/**
 * Comprehensive event taxonomy with human-readable names for analytics tracking
 * Following best practices from Linear, MoonPay, and Raycast
 */
/**
 * Screen transition events - track user navigation flow
 */
export const SCREEN_EVENTS = {
    // Main flow screens
    MAIN_SCREEN_VIEWED: 'main_calculation_screen_viewed',
    NFT_SCREEN_VIEWED: 'nft_purchase_screen_viewed',
    PHONE_SCREEN_VIEWED: 'phone_verification_screen_viewed',
    PHONE_OTP_SCREEN_VIEWED: 'phone_otp_verification_screen_viewed',
    EMAIL_SCREEN_VIEWED: 'email_verification_screen_viewed',
    EMAIL_OTP_SCREEN_VIEWED: 'email_otp_verification_screen_viewed',
    ADDRESS_SCREEN_VIEWED: 'wallet_address_screen_viewed',
    KYC_SCREEN_VIEWED: 'kyc_verification_screen_viewed',
    KYC_SIMPLE_SCREEN_VIEWED: 'simplified_kyc_screen_viewed',
    KYC_PENDING_SCREEN_VIEWED: 'kyc_pending_screen_viewed',
    ORDER_REVIEW_SCREEN_VIEWED: 'order_review_screen_viewed',
    PAYMENT_SCREEN_VIEWED: 'payment_method_screen_viewed',
    PAYMENT_3DS_SCREEN_VIEWED: 'payment_3ds_authentication_screen_viewed',
    BLOCKED_SCREEN_VIEWED: 'account_blocked_screen_viewed',
    SUCCESS_SCREEN_VIEWED: 'transaction_success_screen_viewed',
    FAILED_SCREEN_VIEWED: 'transaction_failed_screen_viewed',
    ERROR_SCREEN_VIEWED: 'error_screen_viewed',
    PAYMENT_PENDING_SCREEN_VIEWED: 'payment_processing_screen_viewed',
    WIRE_PENDING_SCREEN_VIEWED: 'wire_transfer_pending_screen_viewed',
    // Screen transitions
    SCREEN_TRANSITION_STARTED: 'screen_transition_started',
    SCREEN_TRANSITION_COMPLETED: 'screen_transition_completed',
    SCREEN_TRANSITION_FAILED: 'screen_transition_failed',
};
/**
 * User interaction events - clicks, hovers, form submissions
 */
export const INTERACTION_EVENTS = {
    // Button interactions
    PRIMARY_BUTTON_CLICKED: 'primary_action_button_clicked',
    SECONDARY_BUTTON_CLICKED: 'secondary_action_button_clicked',
    BACK_BUTTON_CLICKED: 'navigation_back_button_clicked',
    CLOSE_BUTTON_CLICKED: 'modal_close_button_clicked',
    CONTINUE_BUTTON_CLICKED: 'flow_continue_button_clicked',
    CANCEL_BUTTON_CLICKED: 'action_cancel_button_clicked',
    // Form interactions
    INPUT_FIELD_FOCUSED: 'form_input_field_focused',
    INPUT_FIELD_CHANGED: 'form_input_field_changed',
    INPUT_VALIDATION_FAILED: 'form_input_validation_failed',
    INPUT_VALIDATION_PASSED: 'form_input_validation_passed',
    FORM_SUBMITTED: 'form_submission_attempted',
    FORM_SUBMISSION_SUCCESSFUL: 'form_submission_successful',
    FORM_SUBMISSION_FAILED: 'form_submission_failed',
    // Dropdown and selection events
    DROPDOWN_OPENED: 'dropdown_menu_opened',
    DROPDOWN_CLOSED: 'dropdown_menu_closed',
    DROPDOWN_OPTION_SELECTED: 'dropdown_option_selected',
    DROPDOWN_OPTION_HOVERED: 'dropdown_option_hovered',
    // Payment method interactions
    PAYMENT_METHOD_SELECTED: 'payment_method_selected',
    PAYMENT_METHOD_CHANGED: 'payment_method_changed',
    PAYMENT_FORM_FILLED: 'payment_form_completed',
    PAYMENT_FORM_CLEARED: 'payment_form_cleared',
    // Currency and amount interactions
    CURRENCY_SELECTOR_OPENED: 'currency_selector_opened',
    FROM_CURRENCY_SELECTED: 'source_currency_selected',
    TO_CURRENCY_SELECTED: 'destination_currency_selected',
    AMOUNT_INPUT_FOCUSED: 'amount_input_focused',
    AMOUNT_INPUT_CHANGED: 'amount_input_changed',
    AMOUNT_CALCULATION_TRIGGERED: 'exchange_rate_calculation_triggered',
    // Link and external interactions
    EXTERNAL_LINK_CLICKED: 'external_link_clicked',
    HELP_LINK_CLICKED: 'help_documentation_clicked',
    TERMS_LINK_CLICKED: 'terms_and_conditions_clicked',
    PRIVACY_LINK_CLICKED: 'privacy_policy_clicked',
    // Hover events
    ELEMENT_HOVERED: 'ui_element_hovered',
    TOOLTIP_DISPLAYED: 'tooltip_information_displayed',
    INFO_ICON_HOVERED: 'info_icon_hovered',
};
/**
 * Business logic events - core application functionality
 */
export const BUSINESS_EVENTS = {
    // Order lifecycle
    ORDER_CREATION_STARTED: 'exchange_order_creation_started',
    ORDER_CREATION_COMPLETED: 'exchange_order_creation_completed',
    ORDER_CREATION_FAILED: 'exchange_order_creation_failed',
    ORDER_UPDATED: 'exchange_order_updated',
    ORDER_CANCELLED: 'exchange_order_cancelled',
    ORDER_EXPIRED: 'exchange_order_expired',
    // Exchange and rates
    EXCHANGE_RATES_FETCHED: 'exchange_rates_fetched',
    EXCHANGE_RATES_UPDATED: 'exchange_rates_updated',
    EXCHANGE_RATE_EXPIRED: 'exchange_rate_expired',
    EXCHANGE_CALCULATION_PERFORMED: 'exchange_amount_calculated',
    // Payment processing
    PAYMENT_INITIATED: 'payment_transaction_initiated',
    PAYMENT_PROCESSING: 'payment_transaction_processing',
    PAYMENT_SUCCESSFUL: 'payment_transaction_successful',
    PAYMENT_FAILED: 'payment_transaction_failed',
    PAYMENT_CANCELLED: 'payment_transaction_cancelled',
    PAYMENT_REFUNDED: 'payment_transaction_refunded',
    // Verification processes
    PHONE_VERIFICATION_STARTED: 'phone_number_verification_started',
    PHONE_VERIFICATION_COMPLETED: 'phone_number_verification_completed',
    PHONE_VERIFICATION_FAILED: 'phone_number_verification_failed',
    EMAIL_VERIFICATION_STARTED: 'email_address_verification_started',
    EMAIL_VERIFICATION_COMPLETED: 'email_address_verification_completed',
    EMAIL_VERIFICATION_FAILED: 'email_address_verification_failed',
    KYC_VERIFICATION_STARTED: 'identity_verification_started',
    KYC_VERIFICATION_COMPLETED: 'identity_verification_completed',
    KYC_VERIFICATION_FAILED: 'identity_verification_failed',
    // Address and wallet
    WALLET_ADDRESS_ENTERED: 'destination_wallet_address_entered',
    WALLET_ADDRESS_VALIDATED: 'wallet_address_validation_successful',
    WALLET_ADDRESS_INVALID: 'wallet_address_validation_failed',
    WALLET_ADDRESS_SAVED: 'wallet_address_saved_for_future',
};
/**
 * System and technical events
 */
export const SYSTEM_EVENTS = {
    // Application lifecycle
    APP_INITIALIZED: 'widget_application_initialized',
    APP_READY: 'widget_application_ready',
    APP_ERROR: 'widget_application_error',
    APP_CRASHED: 'widget_application_crashed',
    // API interactions
    API_REQUEST_STARTED: 'api_request_initiated',
    API_REQUEST_COMPLETED: 'api_request_completed',
    API_REQUEST_FAILED: 'api_request_failed',
    API_TIMEOUT: 'api_request_timeout',
    API_RATE_LIMITED: 'api_rate_limit_exceeded',
    // Performance events
    PAGE_LOAD_STARTED: 'page_loading_started',
    PAGE_LOAD_COMPLETED: 'page_loading_completed',
    SLOW_PERFORMANCE_DETECTED: 'slow_performance_detected',
    // Error events
    JAVASCRIPT_ERROR: 'javascript_error_occurred',
    NETWORK_ERROR: 'network_connection_error',
    VALIDATION_ERROR: 'data_validation_error',
    // Browser events
    TAB_VISIBILITY_CHANGED: 'browser_tab_visibility_changed',
    WINDOW_FOCUS_CHANGED: 'browser_window_focus_changed',
    BROWSER_BACK_PRESSED: 'browser_back_button_pressed',
    BROWSER_FORWARD_PRESSED: 'browser_forward_button_pressed',
};
/**
 * User engagement events
 */
export const ENGAGEMENT_EVENTS = {
    // Session events
    SESSION_STARTED: 'user_session_started',
    SESSION_ENDED: 'user_session_ended',
    SESSION_EXTENDED: 'user_session_extended',
    SESSION_TIMEOUT: 'user_session_timeout',
    // User behavior
    IDLE_TIME_DETECTED: 'user_idle_behavior_detected',
    ACTIVE_TIME_TRACKED: 'user_active_time_tracked',
    REPEATED_ACTION: 'user_repeated_action_detected',
    UNUSUAL_PATTERN: 'user_unusual_behavior_pattern',
    // Feature usage
    FEATURE_DISCOVERED: 'new_feature_discovered',
    FEATURE_USED: 'feature_functionality_used',
    FEATURE_ABANDONED: 'feature_usage_abandoned',
    FEATURE_COMPLETED: 'feature_workflow_completed',
    // Help and support
    HELP_REQUESTED: 'user_help_requested',
    FAQ_ACCESSED: 'faq_documentation_accessed',
    SUPPORT_CONTACTED: 'customer_support_contacted',
};
/**
 * Event categories for grouping and filtering
 */
export const EVENT_CATEGORIES = {
    SCREEN: 'screen_navigation',
    INTERACTION: 'user_interaction',
    BUSINESS: 'business_logic',
    SYSTEM: 'system_technical',
    ENGAGEMENT: 'user_engagement',
};
/**
 * Get event category for a given event
 */
export const getEventCategory = (event) => {
    if (Object.values(SCREEN_EVENTS).includes(event)) {
        return EVENT_CATEGORIES.SCREEN;
    }
    if (Object.values(INTERACTION_EVENTS).includes(event)) {
        return EVENT_CATEGORIES.INTERACTION;
    }
    if (Object.values(BUSINESS_EVENTS).includes(event)) {
        return EVENT_CATEGORIES.BUSINESS;
    }
    if (Object.values(SYSTEM_EVENTS).includes(event)) {
        return EVENT_CATEGORIES.SYSTEM;
    }
    if (Object.values(ENGAGEMENT_EVENTS).includes(event)) {
        return EVENT_CATEGORIES.ENGAGEMENT;
    }
    return 'unknown';
};
